import React, { useEffect, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, Typography, Flex, Select, Descriptions, Tag, Spin } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import type { Pixel } from '@/data'
import { getPixels } from '@/api/lop/pixels/getPixels'
import type { FlowEditorContext } from '../../index'

const { Title, Text } = Typography

export const LeadFlowPixelsPage: React.FC = () => {
  const { selectedPixelIds, setSelectedPixelIds } =
    useOutletContext<FlowEditorContext>()

  const [pixels, setPixels] = useState<Pixel[]>([])
  const [loading, setLoading] = useState(true)
  const hasFetched = useRef(false)

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    ;(async () => {
      try {
        const { data } = await getPixels()
        setPixels(data)
      } catch (err) {
        console.error('Failed to load pixels', err)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const selectedPixels = selectedPixelIds
    .map((id) => pixels.find((p) => p.id === id))
    .filter(Boolean) as Pixel[]

  return (
    <div>
      <Flex vertical gap="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Pixels</Title>
          <Text type="secondary">
            Select pixels to attach to this lead flow for tracking conversions.
          </Text>
        </div>

        <Spin spinning={loading}>
          <Card title="Select Pixels" style={{ maxWidth: 1100 }}>
            <Flex vertical gap="middle" style={{ width: '100%' }}>
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder="Search for pixels..."
                value={selectedPixelIds}
                onChange={(values) => setSelectedPixelIds(values)}
                style={{ width: '100%' }}
                size="large"
                suffixIcon={<SearchOutlined />}
                filterOption={(input, option) => {
                  const pixel = pixels.find((p) => p.id === option?.value)
                  if (!pixel) return false
                  return pixel.name.toLowerCase().includes(input.toLowerCase())
                }}
                options={pixels.map((pixel) => ({
                  value: pixel.id,
                  label: pixel.name,
                }))}
              />

              {selectedPixels.map((pixel) => (
                <Card
                  key={pixel.id}
                  size="small"
                  style={{
                    backgroundColor: '#f6ffed',
                    borderColor: '#b7eb8f',
                  }}
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Name">
                      {pixel.name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Pixel ID">
                      <Text code>{pixel.pixel_id}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Partner">
                      {pixel.partner?.name ?? '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Events">
                      <Flex gap="small" wrap="wrap">
                        {pixel.event_mappings.map((event, i) => (
                          <Tag key={i} color="blue">
                            {event.inbound_event} → {event.outbound_event}
                          </Tag>
                        ))}
                      </Flex>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ))}
            </Flex>
          </Card>
        </Spin>
      </Flex>
    </div>
  )
}
